import { useEffect, useRef, useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { unwrapFunctionInvoke, getFriendlyMessage, logError } from '@/lib/error-utils';

const SYNC_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const CONSECUTIVE_FAILURE_THRESHOLD = 3;

// Sync status types
export type CalendarSyncStatus = 'success' | 'partial' | 'failed' | 'never_synced';

export interface CalendarSyncState {
  status: CalendarSyncStatus;
  lastSyncAt: Date | null;
  errorMessage: string | null;
  eventsSynced: number;
  eventsFailed: number;
  sessionsCreated: number;
  sessionsUpdated: number;
  sessionsDeleted: number;
  leadsCreated: number;
  consecutiveFailures: number;
}

export function useCalendarSync() {
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasSyncedOnLogin = useRef(false);
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);

  // Fetch sync status from database
  const { data: syncStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['calendar-sync-status'],
    queryFn: async (): Promise<CalendarSyncState | null> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;

      const { data, error } = await supabase
        .from('calendar_sync_status')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error || !data) {
        return {
          status: 'never_synced',
          lastSyncAt: null,
          errorMessage: null,
          eventsSynced: 0,
          eventsFailed: 0,
          sessionsCreated: 0,
          sessionsUpdated: 0,
          sessionsDeleted: 0,
          leadsCreated: 0,
          consecutiveFailures: 0,
        };
      }

      return {
        status: data.status as CalendarSyncStatus,
        lastSyncAt: data.last_sync_at ? new Date(data.last_sync_at) : null,
        errorMessage: data.error_message,
        eventsSynced: data.events_synced || 0,
        eventsFailed: data.events_failed || 0,
        sessionsCreated: data.sessions_created || 0,
        sessionsUpdated: data.sessions_updated || 0,
        sessionsDeleted: data.sessions_deleted || 0,
        leadsCreated: data.leads_created || 0,
        consecutiveFailures: data.consecutive_failures || 0,
      };
    },
    staleTime: 30000, // 30 seconds
  });

  const runSync = useCallback(async (silent = false) => {
    if (isSyncing) return;

    try {
      setIsSyncing(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Check if user has calendar connected
      const { data: tokens } = await supabase
        .from('google_calendar_tokens')
        .select('id')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (!tokens) return; // No calendar connection

      const invokeResult = await supabase.functions.invoke('calendar-import', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      // Refresh sync status from database
      await refetchStatus();

      const data = unwrapFunctionInvoke<{
        syncStatus?: string;
        sessionsCreated?: number;
        sessionsUpdated?: number;
        leadsCreated?: number;
      }>(invokeResult);

      if (!silent) {
        // Show notification based on result
        if (data?.syncStatus === 'success') {
          const changesCount = (data.sessionsCreated || 0) + (data.sessionsUpdated || 0) + (data.leadsCreated || 0);
          if (changesCount > 0) {
            toast.success('Calendar synced', {
              description: `${changesCount} event${changesCount !== 1 ? 's' : ''} synced`,
            });
          }
        }
      }

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });

    } catch (error: any) {
      // Always capture, even on silent background syncs.
      logError(error, 'calendar-sync');

      // Refresh sync status to get error details from the database
      await refetchStatus();

      if (!silent) {
        // Surface the actual error so the user knows what went wrong, plus a
        // copyable code and a retry action.
        const f = getFriendlyMessage(error);
        toast.error(f.title, {
          description: `${f.userMessage} (code: ${f.code})`,
          duration: 8000,
          action: {
            label: 'Retry',
            onClick: () => runSync(false),
          },
        });
      }
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, refetchStatus, queryClient]);

  // Show alert for persistent failures
  useEffect(() => {
    if (syncStatus?.consecutiveFailures && syncStatus.consecutiveFailures >= CONSECUTIVE_FAILURE_THRESHOLD) {
      toast.error(`Calendar sync failed ${syncStatus.consecutiveFailures} times in a row`, {
        description: syncStatus.errorMessage || 'Your Google Calendar token may have expired — try disconnecting and reconnecting in Settings.',
        duration: 15000,
        action: {
          label: 'Retry',
          onClick: () => runSync(false),
        },
      });
    }
  }, [syncStatus?.consecutiveFailures, syncStatus?.errorMessage, runSync]);

  useEffect(() => {
    // Sync on login (first mount when authenticated)
    const syncOnLogin = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session && !hasSyncedOnLogin.current) {
        hasSyncedOnLogin.current = true;
        await runSync();
      }
    };

    syncOnLogin();

    // Set up interval sync every 10 minutes
    syncIntervalRef.current = setInterval(() => {
      runSync(true);
    }, SYNC_INTERVAL_MS);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [runSync]);

  return {
    runSync,
    isSyncing,
    syncStatus,
    refetchStatus,
  };
}

// Format last sync time for display
export function formatLastSyncTime(date: Date | null): string {
  if (!date) return 'Never';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}
